import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Team } from '@spectrace/shared';

/**
 * Service to manage the currently selected team context
 */
@Injectable({
  providedIn: 'root',
})
export class TeamContextService {
  private currentTeamSubject = new BehaviorSubject<Team | null>(null);
  private userTeamsSubject = new BehaviorSubject<Team[]>([]);

  public currentTeam$ = this.currentTeamSubject.asObservable();
  public userTeams$ = this.userTeamsSubject.asObservable();

  /**
   * Get the currently selected team
   */
  getCurrentTeam(): Team | null {
    return this.currentTeamSubject.value;
  }

  /**
   * Get the current team ID
   */
  getCurrentTeamId(): string | null {
    return this.currentTeamSubject.value?.id || null;
  }

  /**
   * Set the currently selected team
   */
  setCurrentTeam(team: Team | null): void {
    this.currentTeamSubject.next(team);

    // Store in localStorage for persistence
    if (team) {
      localStorage.setItem('selectedTeamId', team.id);
    } else {
      localStorage.removeItem('selectedTeamId');
    }
  }

  /**
   * Set all user teams and optionally select one
   */
  setUserTeams(teams: Team[]): void {
    this.userTeamsSubject.next(teams);

    // If there's a stored team ID, try to restore it
    const storedTeamId = localStorage.getItem('selectedTeamId');
    if (storedTeamId) {
      const storedTeam = teams.find((t) => t.id === storedTeamId);
      if (storedTeam) {
        this.setCurrentTeam(storedTeam);
        return;
      }
    }

    // Otherwise, select the first team by default
    if (teams.length > 0 && !this.currentTeamSubject.value) {
      this.setCurrentTeam(teams[0]);
    }
  }

  /**
   * Get all user teams
   */
  getUserTeams(): Team[] {
    return this.userTeamsSubject.value;
  }

  /**
   * Clear team context (on logout)
   */
  clear(): void {
    this.currentTeamSubject.next(null);
    this.userTeamsSubject.next([]);
    localStorage.removeItem('selectedTeamId');
  }
}
